import React, { useCallback, useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Skeleton,
} from "@mui/material";
import {
  Block as BlockIcon,
  BarChart as BarChartIcon,
  SwapVert as SwapVertIcon,
  Timeline as TimelineIcon,
} from "@mui/icons-material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import MonthlyFlowChart from "../../../components/organisms/charts/MonthlyFlowChart";
import TokenBarList from "../../../components/organisms/charts/TokenBarList";
import { FLOW_COLORS, flowColorOf, formatToken } from "../../../components/organisms/charts/tokenChartTheme";
import { getRoleLabel } from "../../../utils/roleLabels";
import { getOperationLabel } from "../../../utils/operationLabels";
import { COLORS, CARD_PAD, CARD_GAP, CARD_PAGE_PAD, dashboardSectionSx } from "../../../styles/uiConstants";
import { useAlert } from "../../../context/AlertProvider";
import { URL } from "../../../store/utils";
import { useHasPermission } from "../../../hooks/useHasPermission";
import {
  ActionButton,
  ChoiceChipGroup,
  FormSelect,
  SearchField,
} from "../../../components/atoms";
import EmptyState from "../../../components/atoms/EmptyState";
import { KpiTile, SectionCard } from "../../../components/molecules";
import { DateCell, TextCell, cellSx, headSx, mainText } from "../../../components/organisms/ClinicRecordList";
import TokenTypeChip, {
  TOKEN_TYPE_LABELS,
} from "../../../components/atoms/TokenTypeChip";
import TokenPricingCard, { PRICING_GROUPS } from "./TokenPricingCard";
import BillUploadDrawer from "./token/BillUploadDrawer";
import FreeUploadDrawer from "./token/FreeUploadDrawer";
import GetReturnDrawer from "./token/GetReturnDrawer";
import {
  useCreateBillUploadMutation,
  useCreateFreeUploadMutation,
  useCreateGetReturnMutation,
} from "../../../store/tokenApi";

// Kart dili (design-principles §4): sabit gövde yüksekliği, uzun liste kart içinde kayar.
const cardSx = { ...dashboardSectionSx, display: "flex", flexDirection: "column", minWidth: 0 };
const TOP_BODY_HEIGHT = 360;
const bottomBodySx = {
  p: CARD_PAD,
  height: 320,
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  overflowY: "auto",
  overscrollBehavior: "contain",
};
const MONO = { fontFamily: "'SF Mono', 'Fira Code', monospace" };

function CardEmpty({ icon, title, description }) {
  return (
    <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <EmptyState variant="illustrated-sm" icon={icon} title={title} description={description} sx={{ py: 0 }} />
    </Box>
  );
}

async function fetchJson(url, token, body = {}) {
  const res = await fetch(
    `${String(URL.url || "").replace(/\/+$/, "")}${url}`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    }
  );
  return res.json();
}

// Modül bazlı sade fiyatlandırma şablonu — server `pricing.modulePrices` ile birebir.
// Aynı modüldeki tüm aksiyonlar (örn. anamnez:create / anamnez:update / anamnez:delete)
// tek fiyatı paylaşır. Özel override'lar hâlâ `pricing.prices` üzerinde tutulur fakat UI
// bu sade modül listesini gösterir. Liste TEK KAYNAK: TokenPricingCard'daki gruplar
// (etiket + açıklama orada yaşıyor), buradaki düz liste onlardan türetilir.
const MODULE_PRICING_FIELDS = PRICING_GROUPS.flatMap((g) => g.fields);

const HISTORY_TYPE_OPTIONS = ["Bill", "FreeUpload", "GetReturn"].map((t) => ({
  value: t,
  label: t === "GetReturn" ? "İadeler" : TOKEN_TYPE_LABELS[t].tr,
}));

const HISTORY_PRESET_OPTIONS = [
  { value: "7d", label: "Son 7 gün" },
  { value: "30d", label: "Son 30 gün" },
  { value: "90d", label: "Son 90 gün" },
];

/**
 * Klinik detayındaki token ekranı. Tek bileşen, İKİ sekmeye hizmet eder
 * (`section`): "Token Ayarı" tüketim şablonu + faturaları, "Token Performansı"
 * analitik grafikleri gösterir. Bakiye/fiyatlandırma isteği ikisinde de gerekli
 * olduğu için ortak; fatura geçmişi yalnız ayar sekmesinde çekilir.
 *
 * @param {"settings"|"performance"} [section]
 */
export default function TokenTab({ clinicId, token, palette, clinic, onBalanceChange, section = "settings" }) {
  const { showSuccess, showError } = useAlert();
  const isSettings = section === "settings";
  const isPerformance = section === "performance";

  /* ── Permissions ── */
  const canViewTokens = useHasPermission("admin-clinics:view-tokens");
  const canLoadTokens = useHasPermission("admin-clinics:load-tokens");
  const canRefundTokens = useHasPermission("admin-clinics:refund-tokens");
  const canViewAnalytics = useHasPermission("admin-clinics:view-token-analytics");
  const canEditPricing = useHasPermission("admin-clinics:update-token-pricing");

  /* ── Balance + analytics state ── */
  const [balance, setBalance] = useState(null);
  const [totalLoaded, setTotalLoaded] = useState(0);
  const [totalConsumed, setTotalConsumed] = useState(0);
  const [monthlyData, setMonthlyData] = useState([]);
  /**
   * Aya göre GİRİŞ / ÇIKIŞ / NET.
   *
   * Sunucu `byType` altında İŞARETLİ toplam döndürür (yükleme +, tüketim/iade −).
   * Eski kod hepsine `Math.abs` uygulayıp tek yığın sütunda topluyordu — giriş
   * ile çıkışı toplamak anlamsız bir rakam üretiyor, net'in işareti de
   * kayboluyordu. Artık işaretine göre iki kovaya ayrılıyor; net = giriş − çıkış.
   */
  const monthlyChartData = useMemo(() => {
    const byMonth = {};
    for (const row of monthlyData) {
      let inflow = 0;
      let outflow = 0;
      Object.values(row.byType || {}).forEach((v) => {
        const n = Number(v) || 0;
        if (n >= 0) inflow += n;
        else outflow += Math.abs(n);
      });
      byMonth[row.month] = { month: row.month, inflow, outflow, net: inflow - outflow };
    }
    // Son 3 ayı sabit olarak inşa et (boş aylar dahil)
    const out = [];
    const now = new Date();
    for (let i = 2; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      out.push(byMonth[key] || { month: key, inflow: 0, outflow: 0, net: 0 });
    }
    return out;
  }, [monthlyData]);

  /** Özet şeridi — dönem (son 3 ay) toplamları. */
  const periodTotals = useMemo(
    () =>
      monthlyChartData.reduce(
        (acc, r) => ({
          inflow: acc.inflow + r.inflow,
          outflow: acc.outflow + r.outflow,
          net: acc.net + r.net,
        }),
        { inflow: 0, outflow: 0, net: 0 }
      ),
    [monthlyChartData]
  );
  const [typeBreakdownData, setTypeBreakdownData] = useState([]);
  const [operationData, setOperationData] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [editedPrices, setEditedPrices] = useState({});
  const [savingPricing, setSavingPricing] = useState(false);
  const [loading, setLoading] = useState(true);

  /* ── Drawer state ── */
  const [billDrawerOpen, setBillDrawerOpen] = useState(false);
  const [freeDrawerOpen, setFreeDrawerOpen] = useState(false);
  const [returnDrawerOpen, setReturnDrawerOpen] = useState(false);

  /* ── RTK Query mutations ── */
  const [createBill, { isLoading: isBillLoading }] =
    useCreateBillUploadMutation();
  const [createFree, { isLoading: isFreeLoading }] =
    useCreateFreeUploadMutation();
  const [createReturn, { isLoading: isReturnLoading }] =
    useCreateGetReturnMutation();

  /* ── History filter state ── */
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyTypes, setHistoryTypes] = useState([]);
  const [historySearch, setHistorySearch] = useState("");
  const [historyPreset, setHistoryPreset] = useState("30d");

  /**
   * Tarih aralığını İSTEK ANINDA hesaplar — useMemo ile ÖNBELLEKLENMEZ.
   *
   * Hata (2026-07-30): aralık `useMemo(..., [historyPreset])` ile sayfa
   * açılışında bir kez hesaplanıyordu, dolayısıyla `to` o ana donuyordu.
   * Sonradan yüklenen bir fatura `createdAt > endDate` olduğu için sunucu
   * filtresine takılıyor ve listede GÖRÜNMÜYORDU; ancak sayfa yenilenip `to`
   * tazelenince ortaya çıkıyordu.
   *
   * Üst sınır GÜNÜN SONUNA çekilir: saat/saniye hassasiyetinde bir üst sınır,
   * istek ile kaydın yazılması arasındaki milisaniyelerde bile aynı tuzağı
   * kurabilir.
   */
  const buildHistoryRange = useCallback(() => {
    const days =
      historyPreset === "7d" ? 7 : historyPreset === "90d" ? 90 : 30;
    const to = new Date();
    to.setHours(23, 59, 59, 999);
    const from = new Date();
    from.setDate(from.getDate() - days);
    from.setHours(0, 0, 0, 0);
    return { from: from.toISOString(), to: to.toISOString() };
  }, [historyPreset]);

  /* ── Consumptions state (kept for the existing "Son Harcamalar" panel) ── */
  const [consumeSearch, setConsumeSearch] = useState("");

  /* ── Fetch balance + analytics ── */
  const fetchAnalytics = async () => {
    if (!token || !clinicId) return;
    setLoading(true);
    try {
      const now = new Date();
      const yearStart = new Date(`${now.getFullYear()}-01-01T00:00:00.000Z`);
      const [balanceRes, monthlyRes, opsRes, pricingRes, typeRes] =
        await Promise.all([
          fetchJson("/api/admin/tokens/balance", token, { clinicId }),
          fetchJson("/api/admin/tokens/analytics/monthly", token, {
            clinicId,
            year: now.getFullYear(),
          }),
          fetchJson("/api/admin/tokens/analytics/operations", token, {
            clinicId,
          }),
          fetchJson("/api/admin/tokens/pricing", token, {}),
          fetchJson("/api/admin/tokens/analytics/by-type", token, {
            clinicId,
            from: yearStart.toISOString(),
            to: now.toISOString(),
          }),
        ]);

      // Balance
      const rawBal = balanceRes?.data?.balance ?? balanceRes?.balance;
      if (rawBal && typeof rawBal === "object") {
        setBalance(rawBal.balance ?? 0);
        setTotalLoaded(rawBal.totalLoaded ?? 0);
        setTotalConsumed(rawBal.totalConsumed ?? 0);
      } else {
        setBalance(rawBal ?? 0);
      }

      // Monthly (new shape: [{ month, byType, net }])
      const monthly = monthlyRes?.data?.monthly || monthlyRes?.data || [];
      setMonthlyData(Array.isArray(monthly) ? monthly : []);

      // Type breakdown
      const breakdown = typeRes?.data?.breakdown || typeRes?.data || [];
      setTypeBreakdownData(Array.isArray(breakdown) ? breakdown : []);

      // Operations
      const ops = opsRes?.data?.operations || opsRes?.data || [];
      setOperationData(Array.isArray(ops) ? ops : []);

      // Pricing — modül bazlı sade görünüm (modulePrices)
      const pricingData =
        pricingRes?.data?.pricing || pricingRes?.data || pricingRes?.pricing;
      const fallbackDefaults = Object.fromEntries(
        MODULE_PRICING_FIELDS.map((f) => [f.key, f.defaultValue])
      );
      if (pricingData) {
        setPricing(pricingData);
        const mp = pricingData.modulePrices || {};
        const obj =
          typeof mp === "object" && !Array.isArray(mp)
            ? mp.toJSON
              ? mp.toJSON()
              : mp
            : {};
        setEditedPrices({ ...fallbackDefaults, ...obj });
      } else {
        setEditedPrices(fallbackDefaults);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  /* ── Fetch history (with filters) ── */
  const fetchHistory = async () => {
    // Fatura listesi yalnız "Token Ayarı" sekmesinde çizilir — performans
    // sekmesinde isteği hiç atmıyoruz.
    if (!token || !clinicId || !isSettings) return;
    setHistoryLoading(true);
    try {
      const range = buildHistoryRange();
      const body = {
        clinicId,
        limit: 100,
        page: 1,
        startDate: range.from,
        endDate: range.to,
      };
      // Server supports a single `type` filter — send first selected, or omit for all
      if (historyTypes.length === 1) {
        body.type = historyTypes[0];
      }
      const res = await fetchJson("/api/admin/tokens/history", token, body);
      const txns =
        res?.data?.transactions || res?.transactions || [];
      setHistory(Array.isArray(txns) ? txns : []);
    } catch {
      // silent
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [token, clinicId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchHistory();
  }, [token, clinicId, historyTypes, historyPreset]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Drawer submit handlers ── */
  const handleBillSubmit = async (payload) => {
    try {
      await createBill({ token, clinicId, ...payload }).unwrap();
      showSuccess("Fatura yüklemesi kaydedildi");
      setBillDrawerOpen(false);
      fetchAnalytics();
      fetchHistory();
      onBalanceChange?.();
    } catch (err) {
      showError(err?.data?.message || "Yükleme başarısız");
    }
  };

  const handleFreeSubmit = async (payload) => {
    try {
      await createFree({ token, clinicId, ...payload }).unwrap();
      showSuccess("Ücretsiz yükleme kaydedildi");
      setFreeDrawerOpen(false);
      fetchAnalytics();
      fetchHistory();
      onBalanceChange?.();
    } catch (err) {
      showError(err?.data?.message || "Yükleme başarısız");
    }
  };

  const handleReturnSubmit = async (payload) => {
    try {
      await createReturn({ token, clinicId, ...payload }).unwrap();
      showSuccess("İade kaydedildi");
      setReturnDrawerOpen(false);
      fetchAnalytics();
      fetchHistory();
      onBalanceChange?.();
    } catch (err) {
      showError(err?.data?.message || "İade başarısız");
    }
  };

  /**
   * Alan boşaltılınca anahtar map'ten SİLİNİR — sunucu o modül için
   * varsayılanı uygular. Sayı olmayan girdi boş string'e düşer.
   */
  const handlePriceChange = useCallback((key, raw) => {
    setEditedPrices((prev) => {
      if (raw === "") {
        const copy = { ...prev };
        delete copy[key];
        return copy;
      }
      const parsed = parseFloat(raw);
      return { ...prev, [key]: Number.isNaN(parsed) ? "" : parsed };
    });
  }, []);

  const handleSavePricing = async () => {
    setSavingPricing(true);
    try {
      const res = await fetchJson("/api/admin/tokens/pricing/update", token, {
        modulePrices: editedPrices,
      });
      if (res?.status === "success" || res?.success) {
        showSuccess("Token fiyatlandırması güncellendi");
      } else {
        showError(res?.message || "Güncelleme başarısız");
      }
    } catch {
      showError("Fiyatlandırma güncellenirken hata oluştu");
    } finally {
      setSavingPricing(false);
    }
  };

  /* ── Chart data ── */
  /**
   * Hareket tipleri — büyükten küçüğe yatay çubuk listesi. Renk TİPE değil
   * YÖNE bağlı: girişler mavi, çıkışlar kırmızı, sıfır etkili düzeltmeler gri.
   */
  const typeBarItems = useMemo(
    () =>
      [...typeBreakdownData]
        .filter((d) => Math.abs(d.total || 0) > 0)
        .sort((a, b) => Math.abs(b.total || 0) - Math.abs(a.total || 0))
        .map((d) => ({
          key: d.type,
          label: TOKEN_TYPE_LABELS[d.type]?.tr || d.type,
          value: Math.abs(d.total || 0),
          color: flowColorOf(d.total || 0),
          note: d.count ? `${d.count} işlem` : undefined,
        })),
    [typeBreakdownData]
  );

  /**
   * En çok tüketen modüller. Hepsi tüketim olduğu için TEK renk kullanılır —
   * sıraya göre renklendirmek (eski tasarım) rengi, çubuk uzunluğunun zaten
   * gösterdiği büyüklüğü tekrar anlatmaya harcıyordu.
   */
  const topConsumptionItems = useMemo(
    () =>
      [...operationData]
        .sort((a, b) => (b.totalTokens || b.total || 0) - (a.totalTokens || a.total || 0))
        .slice(0, 6)
        .map((op, i) => ({
          key: `${op._id || op.operationType || i}`,
          label: getOperationLabel(op._id || op.operationType) || "Diğer",
          value: op.totalTokens || op.total || 0,
          color: FLOW_COLORS.out,
        }))
        .filter((it) => it.value > 0),
    [operationData]
  );

  /* ── History: client-side search + Token Faturaları için sadece Bill/FreeUpload/GetReturn ── */
  const filteredHistory = useMemo(() => {
    const ALLOWED = new Set(["Bill", "FreeUpload", "GetReturn"]);
    const base = history.filter((tx) => ALLOWED.has(tx.type));
    const q = historySearch.toLowerCase().trim();
    if (!q) return base;
    return base.filter((tx) => {
      const billStr = tx.bill
        ? `${tx.bill.billNo || ""} ${tx.bill.firmApellation || ""}`.toLowerCase()
        : "";
      const emailStr = (tx.performedBy?.email || "").toLowerCase();
      return billStr.includes(q) || emailStr.includes(q);
    });
  }, [history, historySearch]);

  /* ── Consumptions (for the existing bottom panel) ── */
  const allConsumptions = useMemo(
    () => history.filter((t) => t.type === "Consumption" || t.type === "consumption"),
    [history]
  );
  const recentConsumptions = useMemo(() => {
    if (!consumeSearch.trim()) return allConsumptions.slice(0, 10);
    const q = consumeSearch.toLowerCase();
    return allConsumptions
      .filter(
        (t) =>
          (t.operationType || "").toLowerCase().includes(q) ||
          getOperationLabel(t.operationType).toLowerCase().includes(q) ||
          (t.performedByRole || "").toLowerCase().includes(q) ||
          String(t.amount).includes(q)
      )
      .slice(0, 10);
  }, [allConsumptions, consumeSearch]);

  /* ── Total revenue from Bill transactions ── */
  const totalRevenue = useMemo(() => {
    return history
      .filter((t) => t.type === "Bill")
      .reduce((sum, t) => {
        const unitPrice = t.bill?.unitPrice;
        const amt = t.amount;
        return sum + (unitPrice && amt ? unitPrice * amt : 0);
      }, 0);
  }, [history]);

  /* ── Permission gate ── */
  // Performans sekmesi tamamen analitikten ibaret; analitik yetkisi yoksa
  // boş bir sayfa yerine yetki uyarısı gösterilir.
  if (!canViewTokens || (isPerformance && !canViewAnalytics)) {
    return (
      <Box sx={{ minHeight: "100%", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: COLORS.slate50 }}>
        <EmptyState
          variant="illustrated"
          icon={<BlockIcon />}
          title="Token bilgileri görüntülenemiyor"
          description="Bu bölümü görüntüleme yetkiniz yok."
        />
      </Box>
    );
  }

  const barSkeleton = (
    <Stack spacing={1.5}>
      {[0, 1, 2, 3].map((i) => (
        <Box key={i}>
          <Skeleton variant="text" width={`${40 + i * 12}%`} height={18} />
          <Skeleton variant="rectangular" height={8} sx={{ borderRadius: 999 }} />
        </Box>
      ))}
    </Stack>
  );

  const barsOrEmpty = (items, emptyTitle, emptyText) =>
    items.length ? (
      <TokenBarList items={items} />
    ) : (
      <CardEmpty icon={<BarChartIcon />} title={emptyTitle} description={emptyText} />
    );

  return (
    <Box
      sx={{
        ...CARD_PAGE_PAD,
        bgcolor: COLORS.slate50,
        minHeight: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: CARD_GAP,
      }}
    >
      {/* ── Token Performansı sekmesi ── */}
      {isPerformance && canViewAnalytics && (
        <>
          {/* KPI satırı: kalan (stok) + son 3 ayın hareketi (akış) */}
          <Box sx={{ display: "grid", gap: CARD_GAP, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" } }}>
            <KpiTile
              loading={loading || balance == null}
              label="Kalan bakiye"
              value={formatToken(balance)}
              hint="Şu an klinikte kullanılabilir"
            />
            <KpiTile loading={loading} label="Yükleme" value={formatToken(periodTotals.inflow)} hint="Son 3 ayda yüklenen" />
            <KpiTile loading={loading} label="Tüketim" value={formatToken(periodTotals.outflow)} hint="Son 3 ayda harcanan" />
            <KpiTile
              loading={loading}
              label="Net"
              value={`${periodTotals.net > 0 ? "+" : ""}${formatToken(periodTotals.net)}`}
              hint="Son 3 ayda bakiyedeki değişim"
              valueColor={periodTotals.net === 0 ? undefined : periodTotals.net > 0 ? FLOW_COLORS.in : FLOW_COLORS.out}
            />
          </Box>

          <SectionCard
            title="Aylık Token Hareketi"
            subtitle="Son 3 ayın yükleme ve tüketimi; mavi giriş, kırmızı çıkış"
            icon={<TimelineIcon sx={{ fontSize: 18 }} />}
            sx={cardSx}
          >
            <Box sx={{ p: CARD_PAD, height: TOP_BODY_HEIGHT, display: "flex", flexDirection: "column", minHeight: 0 }}>
              {loading ? (
                <Box sx={{ flex: 1, display: "flex", gap: 3, alignItems: "flex-end", px: 4, pb: 4 }}>
                  {[70, 45, 90].map((h, i) => (
                    <Skeleton key={i} variant="rectangular" sx={{ flex: 1, height: `${h}%`, borderRadius: "4px 4px 0 0" }} />
                  ))}
                </Box>
              ) : (
                <MonthlyFlowChart data={monthlyChartData} height={TOP_BODY_HEIGHT - 64} />
              )}
            </Box>
          </SectionCard>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: CARD_GAP }}>
            <SectionCard
              title="Hareket Tipleri"
              subtitle="Bu yıl işlem tipine göre toplam token"
              icon={<SwapVertIcon sx={{ fontSize: 18 }} />}
              sx={cardSx}
            >
              <Box sx={bottomBodySx}>
                {loading ? barSkeleton : barsOrEmpty(typeBarItems, "Bu yıl hareket yok", "Token yüklendikçe veya harcandıkça burada görünür.")}
              </Box>
            </SectionCard>

            <SectionCard
              title="En Çok Tüketen Modüller"
              subtitle="Tokenın harcandığı ilk altı modül"
              icon={<BarChartIcon sx={{ fontSize: 18 }} />}
              sx={cardSx}
            >
              <Box sx={bottomBodySx}>
                {loading ? barSkeleton : barsOrEmpty(topConsumptionItems, "Henüz tüketim yok", "Klinik ücretli işlemleri kullandıkça burada görünür.")}
              </Box>
            </SectionCard>
          </Box>
        </>
      )}

      {/* ── Token Tüketim Şablonu — yalnız "Token Ayarı" sekmesinde ── */}
      {isSettings && (
        <TokenPricingCard
          editedPrices={editedPrices}
          onPriceChange={handlePriceChange}
          canEdit={canEditPricing}
          saving={savingPricing}
          onSave={handleSavePricing}
          palette={palette}
        />
      )}

      {/* ── Token Faturaları — yalnız "Token Ayarı" sekmesinde ── */}
      {isSettings && (
      <SectionCard
        title="Token Hareketleri"
        subtitle="Fatura ile yükleme, ücretsiz yükleme ve iadeler"
        icon={<ReceiptLongIcon sx={{ fontSize: 18 }} />}
        sx={{ ...cardSx, height: 560, overflow: "hidden" }}
        action={
          (canLoadTokens || canRefundTokens) ? (
            <Stack direction="row" spacing={1}>
              {canLoadTokens && (
                <ActionButton variant="add" compact palette={palette} icon={<ReceiptLongIcon sx={{ fontSize: 15 }} />} onClick={() => setBillDrawerOpen(true)} label="Fatura Yükle" />
              )}
              {canLoadTokens && (
                <ActionButton variant="add" compact tone="soft" palette={palette} icon={<CardGiftcardIcon sx={{ fontSize: 15 }} />} onClick={() => setFreeDrawerOpen(true)} label="Ücretsiz Yükle" />
              )}
              {canRefundTokens && (
                <ActionButton variant="delete" compact tone="outline" icon={<CurrencyExchangeIcon sx={{ fontSize: 15 }} />} onClick={() => setReturnDrawerOpen(true)} label="İade" />
              )}
            </Stack>
          ) : null
        }
      >
        {/* Araç satırı: tip çipleri + dönem + arama */}
        <Box
          sx={{
            px: CARD_PAD,
            py: 1.5,
            display: "flex",
            gap: 1.5,
            flexWrap: "wrap",
            alignItems: "center",
            borderBottom: `1px solid ${COLORS.slate100}`,
            flexShrink: 0,
          }}
        >
          <ChoiceChipGroup
            multiple
            ariaLabel="İşlem tipi filtresi"
            options={HISTORY_TYPE_OPTIONS}
            value={historyTypes}
            onChange={setHistoryTypes}
          />
          <Box sx={{ flex: 1 }} />
          <FormSelect
            palette={palette}
            value={historyPreset}
            onChange={setHistoryPreset}
            options={HISTORY_PRESET_OPTIONS}
            placeholder={null}
            fullWidth={false}
            sx={{ width: 150 }}
            inputProps={{ "aria-label": "Dönem" }}
          />
          <SearchField
            placeholder="Fatura no, firma veya e-posta"
            value={historySearch}
            onChange={setHistorySearch}
            sx={{ width: { xs: "100%", sm: 260 } }}
          />
        </Box>

        {/* Hareket tablosu — kart içinde kayar */}
        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", overscrollBehavior: "contain" }}>
          {!(loading || historyLoading) && filteredHistory.length === 0 ? (
            <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <EmptyState
                variant="illustrated-sm"
                icon={<ReceiptLongIcon />}
                title="İşlem bulunamadı"
                description={historySearch ? `“${historySearch}” aramasıyla eşleşen işlem yok.` : "Seçili dönem ve tiplerde token hareketi yok."}
              />
            </Box>
          ) : (
            <Table stickyHeader size="small" sx={{ tableLayout: "fixed", minWidth: 820 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headSx, pl: CARD_PAD, width: "13%" }}>Fatura no</TableCell>
                  <TableCell sx={{ ...headSx, width: "14%" }}>Tarih</TableCell>
                  <TableCell sx={{ ...headSx, width: "16%" }}>Tip</TableCell>
                  <TableCell align="right" sx={{ ...headSx, width: "12%" }}>Miktar</TableCell>
                  <TableCell align="right" sx={{ ...headSx, width: "13%" }}>Sonraki bakiye</TableCell>
                  <TableCell sx={{ ...headSx, pl: 3, pr: CARD_PAD, width: "32%" }}>İşlemi yapan</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(loading || historyLoading) ? (
                  [0, 1, 2, 3, 4].map((i) => (
                    <TableRow key={i}>
                      {[0, 1, 2, 3, 4, 5].map((c) => (
                        <TableCell key={c} sx={{ ...cellSx, ...(c === 0 ? { pl: CARD_PAD } : {}) }}>
                          <Skeleton variant="text" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  filteredHistory.map((tx) => {
                    const by = tx.performedBy?.name ? `${tx.performedBy.name} ${tx.performedBy.surname || ""}`.trim() : null;
                    return (
                      <TableRow key={tx._id}>
                        <TableCell sx={{ ...cellSx, pl: CARD_PAD }}>
                          <Typography sx={{ ...mainText, ...MONO }} noWrap>
                            {tx.bill?.billNo ? `#${tx.bill.billNo}` : "—"}
                          </Typography>
                        </TableCell>
                        <DateCell value={tx.createdAt} />
                        <TableCell sx={cellSx}>
                          <TokenTypeChip type={tx.type} />
                        </TableCell>
                        <TableCell align="right" sx={cellSx}>
                          <Typography
                            sx={{
                              ...mainText,
                              fontWeight: 600,
                              fontVariantNumeric: "tabular-nums",
                              color: tx.amount >= 0 ? FLOW_COLORS.in : FLOW_COLORS.out,
                            }}
                          >
                            {tx.amount > 0 ? "+" : ""}
                            {formatToken(tx.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={cellSx}>
                          <Typography sx={{ ...mainText, fontVariantNumeric: "tabular-nums" }}>
                            {tx.balanceAfter != null ? formatToken(tx.balanceAfter) : "—"}
                          </Typography>
                        </TableCell>
                        <TextCell
                          sx={{ pl: 3, pr: CARD_PAD }}
                          main={by}
                          sub={tx.performedByRole ? getRoleLabel(tx.performedByRole) : null}
                        />
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </Box>
      </SectionCard>
      )}

      {/* ── Drawers ── */}
      <BillUploadDrawer
        open={billDrawerOpen}
        onClose={() => setBillDrawerOpen(false)}
        clinic={clinic}
        onSubmit={handleBillSubmit}
        isLoading={isBillLoading}
      />
      <FreeUploadDrawer
        open={freeDrawerOpen}
        onClose={() => setFreeDrawerOpen(false)}
        clinic={clinic}
        onSubmit={handleFreeSubmit}
        isLoading={isFreeLoading}
      />
      <GetReturnDrawer
        open={returnDrawerOpen}
        onClose={() => setReturnDrawerOpen(false)}
        clinic={clinic}
        currentBalance={balance ?? 0}
        onSubmit={handleReturnSubmit}
        isLoading={isReturnLoading}
      />
    </Box>
  );
}
